import { useEffect, useState, useCallback } from "react";
import { Search, Loader2, ShieldCheck, Plus, Globe, Building2 } from "lucide-react";
import { DataTable } from "@/components/common/DataTable";
import { StatCard } from "@/components/common/StatCard";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { api, listResource, createResource } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import AltasPlanCard from "./AltasPlanCard";
import BarridoListasCard from "./BarridoListasCard";
import DescarteUmbralBloque from "./DescarteUmbralBloque";
import { FichaCoincidencia } from "./FichaCoincidencia";

const TIPOS_INTERNA = ["Clientes rechazados", "Terceros de alto riesgo", "Empleados con incidencias"];

function ScoreCell({ value }) {
  const color = value >= 90 ? "text-red-600" : value >= 70 ? "text-amber-600" : "text-slate-600";
  return <span className={`font-semibold ${color}`} data-testid="match-score">{value}</span>;
}

export default function ScreeningPanel({ barridoRefresh = 0, barridoJob = null }) {
  const { user, can } = useAuth();
  const esOC = ["super_admin", "oficial_cumplimiento"].includes(user?.role);
  const puedeListas = can("listas_internas", "crear");

  const [stats, setStats] = useState(null);
  const [matches, setMatches] = useState([]);
  const [globales, setGlobales] = useState([]);
  const [internas, setInternas] = useState([]);
  const [internasEntradas, setInternasEntradas] = useState([]);

  // buscador
  const [q, setQ] = useState("");
  const [buscando, setBuscando] = useState(false);
  const [resultados, setResultados] = useState(null);

  // ficha de la coincidencia seleccionada
  const [sel, setSel] = useState(null);

  // feb-2026 (P0-B) — Estado del cotejo externo Q-Detect. Cuando hay `total_pendientes>0`,
  // la bandeja no puede pintarse en verde: el "Sin coincidencias" sería falso positivo.
  const [qeqEstado, setQeqEstado] = useState(null);

  // feb-2026: jerarquía de coincidencias — parciales (media/baja no críticas) detrás de un toggle.
  // Las críticas SIEMPRE se muestran, independientemente del score (candado regulatorio LPB/OFAC/ONU/SAT).
  const [mostrarParciales, setMostrarParciales] = useState(false);

  // nueva lista interna / entrada
  const [openLista, setOpenLista] = useState(false);
  const [nuevaLista, setNuevaLista] = useState({ nombre_lista: "", tipo: TIPOS_INTERNA[0] });
  const [openEntrada, setOpenEntrada] = useState(false);
  const [nuevaEntrada, setNuevaEntrada] = useState({ lista_id: "", nombre: "", rfc: "", observaciones: "" });

  const loadAll = useCallback(async () => {
    const [s, m, g, li, qe] = await Promise.all([
      api.get("/screening/dashboard").then((r) => r.data),
      api.get("/screening/matches").then((r) => r.data),
      api.get("/screening/listas-globales").then((r) => r.data),
      listResource("listas_internas"),
      api.get("/screening/qeq/estado").then((r) => r.data).catch(() => null),
    ]);
    setStats(s); setMatches(m); setGlobales(g); setInternas(li);
    setQeqEstado(qe);
    setInternasEntradas(await listResource("listas_internas_entradas"));
  }, []);

  useEffect(() => { loadAll(); }, [loadAll, barridoRefresh]);

  const buscar = async () => {
    if (!q.trim()) return;
    setBuscando(true);
    try {
      const { data } = await api.post("/screening/buscar", { q, mostrar_todo: true });
      setResultados(data.matches);
      if (!data.matches.length) toast.info("Sin coincidencias para la búsqueda");
    } catch (e) { toast.error("No se pudo buscar"); }
    finally { setBuscando(false); }
  };

  const crearLista = async () => {
    if (!nuevaLista.nombre_lista.trim()) { toast.error("Indica el nombre de la lista"); return; }
    await createResource("listas_internas", {
      nombre_lista: nuevaLista.nombre_lista, tipo: nuevaLista.tipo, version: "1", vigente: true,
      total_registros: 0, metodo_actualizacion: "manual", fecha_carga: new Date().toISOString().slice(0, 10),
    });
    toast.success("Lista interna creada");
    setOpenLista(false); setNuevaLista({ nombre_lista: "", tipo: TIPOS_INTERNA[0] });
    await loadAll();
  };

  const crearEntrada = async () => {
    if (!nuevaEntrada.lista_id || !nuevaEntrada.nombre.trim()) { toast.error("Selecciona lista y nombre"); return; }
    const lista = internas.find((l) => l.id === nuevaEntrada.lista_id);
    await createResource("listas_internas_entradas", {
      ...nuevaEntrada, nombre_lista: lista?.nombre_lista, alias: [],
    });
    toast.success("Registro agregado a la lista interna");
    setOpenEntrada(false); setNuevaEntrada({ lista_id: "", nombre: "", rfc: "", observaciones: "" });
    await loadAll();
  };

  const MATCH_COLS = [
    { key: "persona_nombre", label: "Persona" },
    { key: "persona_tipo", label: "Tipo", kind: "badge" },
    { key: "lista_nombre", label: "Lista", kind: "badge" },
    { key: "score", label: "Score", render: (r) => <ScoreCell value={r.score} /> },
    { key: "tipo_coincidencia", label: "Coincidencia", kind: "badge" },
    { key: "estatus", label: "Decisión", render: (r) => <StatusBadge value={ESTATUS_LABEL[r.estatus] || r.estatus} /> },
    { key: "critica", label: "Crítica", render: (r) => (r.critica ? "Sí" : "No") },
  ];

  return (
    <div data-testid="screening-panel">
      {/* Disclaimer */}
      <div className="mb-4 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
        FELVORA es una herramienta de apoyo; la obligación de cumplimiento corresponde al sujeto obligado. Resultados en modo de apoyo, sujetos a validación humana. Las consultas a proveedores externos se realizan conforme a la configuración contratada por la entidad.
      </div>

      {/* feb-2026 (P0-B) — Estado del cotejo externo. Cuando hay clientes/prospectos sin
          cotejo externo completado, la bandeja no representa "todos verificados". Este
          aviso lo pinta imposible de ignorar antes de mirar la tabla. */}
      {qeqEstado && qeqEstado.total_pendientes > 0 && (
        <div data-testid="scr-cotejo-pendiente-banner"
             className="mb-4 flex items-start gap-2 rounded-md border-2 border-amber-400 bg-amber-50 px-3 py-2.5 text-sm text-amber-900">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
          <div>
            <p className="font-semibold uppercase tracking-wide text-xs">
              Cotejo externo pendiente en {qeqEstado.total_pendientes} persona(s)
            </p>
            <p className="text-xs">
              {qeqEstado.pendientes_clientes?.length || 0} cliente(s) y {qeqEstado.pendientes_prospectos?.length || 0} prospecto(s)
              no completaron la consulta de cotejo externo. Mientras el proveedor no responda,
              la ausencia de coincidencias en esta bandeja <b>no equivale a "limpio"</b>. Cotejo externo activo:{" "}
              <span className="font-mono">{qeqEstado.qeq_activo ? "sí" : "NO"}</span>.
            </p>
          </div>
        </div>
      )}

      {/* KPIs */}
      {stats && (
        <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <StatCard label="Pendientes" value={stats.pendientes} icon="ListChecks" accent="primary" />
          <StatCard label="Críticas pendientes" value={stats.criticas_pendientes} icon="AlertTriangle" accent="red" />
          <StatCard label="Confirmadas" value={stats.confirmadas} icon="ShieldX" accent="red" />
          <StatCard label="Falsos positivos" value={stats.falsos_positivos} icon="CheckCircle2" accent="green" />
          <StatCard label="Clientes bloqueados" value={stats.clientes_bloqueados} icon="Ban" accent="red" />
          <StatCard label="Total coincidencias" value={stats.total_coincidencias} icon="Search" accent="primary" />
        </div>
      )}

      <div className="mb-4">
        <AltasPlanCard />
      </div>

      <div className="mb-4">
        <BarridoListasCard refreshKey={barridoRefresh} job={barridoJob} />
      </div>

      <Tabs defaultValue="bandeja">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <TabsList className="h-auto flex-wrap">
            <TabsTrigger value="bandeja" data-testid="scr-tab-bandeja">Coincidencias</TabsTrigger>
            <TabsTrigger value="buscador" data-testid="scr-tab-buscador">Buscador manual</TabsTrigger>
            <TabsTrigger value="globales" data-testid="scr-tab-globales">Listas globales</TabsTrigger>
            <TabsTrigger value="internas" data-testid="scr-tab-internas">Listas internas</TabsTrigger>
          </TabsList>
          <p className="w-full text-xs text-slate-400 sm:w-auto sm:text-right" data-testid="scr-nota-barrido">
            El cotejo masivo se ejecuta con «Correr barrido», en el encabezado de PLD.
          </p>
        </div>

        {/* Bandeja de coincidencias */}
        <TabsContent value="bandeja">
          {/* oct-2026 · Semáforo por similitud (rojo >= 90 · ámbar 70-89 · gris < 70) con el
              conteo por color, y el descarte en bloque por umbral del Oficial de Cumplimiento. */}
          <DescarteUmbralBloque matches={matches} onDone={loadAll} />
          {(() => {
            // feb-2026: jerarquía. Reglas:
            //  - Críticas (LPB/OFAC/ONU/SAT 69/69-B) SIEMPRE se muestran, sin importar score.
            //  - No críticas con nivel exacta/alta → arriba, en la sección principal.
            //  - No críticas con nivel media/baja → detrás del toggle "ver parciales".
            const principales = matches.filter((r) => r.critica || r.nivel === "exacta" || r.nivel === "alta");
            const parciales = matches.filter((r) => !r.critica && (r.nivel === "media" || r.nivel === "baja"));
            const hayAltas = principales.length > 0;
            return (
              <>
                {!hayAltas && parciales.length === 0 && (
                  qeqEstado && qeqEstado.total_pendientes > 0 ? (
                    <div data-testid="scr-sin-verificar-tabla"
                         className="rounded-md border border-amber-300 bg-amber-50 px-4 py-6 text-center text-sm text-amber-900">
                      <p className="font-semibold">Bandeja vacía — pero hay cotejos externos PENDIENTES.</p>
                      <p className="mt-1 text-xs">
                        No hay coincidencias registradas en listas locales, y {qeqEstado.total_pendientes} persona(s)
                        no completaron el cotejo externo. Este estado <b>no</b> es "limpio";
                        es "sin verificar".
                      </p>
                    </div>
                  ) : (
                    <div data-testid="scr-sin-coincidencias" className="rounded-md border border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
                      Sin coincidencias. Corre el barrido de listas desde el encabezado de PLD.
                    </div>
                  )
                )}
                {!hayAltas && parciales.length > 0 && (
                  <div data-testid="scr-sin-coincidencias-relevantes" className="mb-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
                    Sin coincidencias relevantes. Existen {parciales.length} coincidencia(s) parcial(es) con score bajo o medio en listas no críticas — visibles al activar el control de abajo.
                  </div>
                )}
                {hayAltas && (
                  <DataTable testid="table-screening-matches" columns={MATCH_COLS} rows={principales}
                    onRowClick={setSel}
                    emptyLabel="Sin coincidencias relevantes." />
                )}
                {parciales.length > 0 && (
                  <div className="mt-4">
                    <Button data-testid="scr-toggle-parciales" size="sm" variant="outline"
                      onClick={() => setMostrarParciales((v) => !v)}
                      className="text-xs">
                      {mostrarParciales ? "Ocultar" : "Ver"} coincidencias parciales ({parciales.length})
                    </Button>
                    {mostrarParciales && (
                      <div className="mt-3 rounded-md border border-slate-200 bg-slate-50/60 p-2">
                        <p className="mb-2 text-[11px] text-slate-500">
                          Coincidencias con score debajo del umbral configurado para listas <b>no</b> críticas. No representan alerta activa; útiles para investigación manual.
                        </p>
                        <DataTable testid="table-screening-matches-parciales" columns={MATCH_COLS} rows={parciales}
                          onRowClick={setSel}
                          emptyLabel="Sin coincidencias parciales." />
                      </div>
                    )}
                  </div>
                )}
              </>
            );
          })()}
          <p className="mt-3 text-xs text-slate-400">Haz clic en una coincidencia para ver su ficha y dictaminarla. Solo el Oficial de Cumplimiento decide coincidencias críticas.</p>
        </TabsContent>

        {/* Buscador manual */}
        <TabsContent value="buscador">
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input data-testid="scr-buscar-input" placeholder="Nombre completo o RFC a verificar..."
              value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && buscar()} />
            <Button data-testid="scr-buscar-btn" onClick={buscar} disabled={buscando} className="bg-primary hover:bg-sidebar-hover">
              {buscando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />} Buscar en listas
            </Button>
          </div>
          {resultados && (
            <div className="mt-4">
              <DataTable testid="table-screening-buscar" rows={resultados}
                columns={[
                  { key: "lista_nombre", label: "Lista", kind: "badge" },
                  { key: "entrada_nombre", label: "Registro en lista" },
                  { key: "score", label: "Score", render: (r) => <ScoreCell value={r.score} /> },
                  { key: "tipo_coincidencia", label: "Tipo", kind: "badge" },
                  { key: "sello", label: "Sello de versión" },
                ]} emptyLabel="Sin coincidencias" />
            </div>
          )}
        </TabsContent>

        {/* Listas globales (solo lectura) */}
        <TabsContent value="globales">
          <div className="mb-2 flex items-center gap-2 text-sm text-slate-500">
            <Globe className="h-4 w-4" /> Listas mantenidas por la plataforma (solo lectura). Las carga/versiona el Super Administrador.
          </div>
          <DataTable testid="table-listas-globales" rows={globales}
            columns={[
              { key: "nombre_lista", label: "Lista" },
              { key: "version", label: "Versión vigente" },
              { key: "fecha_carga", label: "Fecha", kind: "date" },
              { key: "total_registros", label: "Registros" },
              { key: "critica", label: "Crítica", render: (r) => (r.critica ? "Sí" : "No") },
              { key: "metodo_actualizacion", label: "Método", kind: "badge" },
              { key: "vigente", label: "Vigente", render: (r) => <StatusBadge value={r.vigente ? "Vigente" : "Histórica"} /> },
            ]} emptyLabel="Sin listas globales cargadas" />
        </TabsContent>

        {/* Listas internas */}
        <TabsContent value="internas">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Building2 className="h-4 w-4" /> Listas internas de tu entidad. Aislamiento estricto: nunca se cruzan con las de otra entidad.
            </div>
            <div className="flex gap-2">
              <Button data-testid="scr-nueva-lista-btn" size="sm" variant="outline" onClick={() => setOpenLista(true)} disabled={!puedeListas}>
                <Plus className="mr-1.5 h-4 w-4" /> Nueva lista
              </Button>
              <Button data-testid="scr-nueva-entrada-btn" size="sm" variant="outline" onClick={() => setOpenEntrada(true)} disabled={!internas.length || !puedeListas}>
                <Plus className="mr-1.5 h-4 w-4" /> Agregar registro
              </Button>
            </div>
          </div>
          <DataTable testid="table-listas-internas" rows={internas}
            columns={[
              { key: "nombre_lista", label: "Lista" },
              { key: "tipo", label: "Tipo", kind: "badge" },
              { key: "total_registros", label: "Registros" },
              { key: "fecha_carga", label: "Carga", kind: "date" },
            ]} emptyLabel="Sin listas internas. Crea una para empezar." />
          <div className="mt-4">
            <p className="mb-2 text-xs font-medium text-slate-500">Registros internos</p>
            <DataTable testid="table-listas-internas-entradas" rows={internasEntradas}
              columns={[
                { key: "nombre_lista", label: "Lista", kind: "badge" },
                { key: "nombre", label: "Nombre" },
                { key: "rfc", label: "RFC" },
                { key: "observaciones", label: "Observaciones" },
              ]} emptyLabel="Sin registros internos" />
          </div>
        </TabsContent>
      </Tabs>

      {/* oct-2026 · Ficha de la coincidencia: registro, comparativo de identidad, dictamen y constancia. */}
      <Dialog open={!!sel} onOpenChange={(v) => !v && setSel(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl" data-testid="match-dialog">
          {sel && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 font-heading">
                  <Search className="h-5 w-5 text-primary" /> Coincidencia — {sel.persona_nombre}
                </DialogTitle>
                <DialogDescription className="sr-only">Detalle de la coincidencia en listas, comparativo de identidad y dictamen</DialogDescription>
              </DialogHeader>
              {sel.fuente_externa === "vigilancia_manual" && <OrigenVigilancia extra={sel.entrada_extra || {}} />}
              <FichaCoincidencia matchId={sel.id} onDecided={loadAll} />
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog nueva lista interna */}
      <Dialog open={openLista} onOpenChange={setOpenLista}>
        <DialogContent className="sm:max-w-md" data-testid="nueva-lista-dialog">
          <DialogHeader><DialogTitle className="font-heading">Nueva lista interna</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="mb-1 block text-xs">Nombre de la lista</Label>
              <Input data-testid="nueva-lista-nombre" value={nuevaLista.nombre_lista}
                onChange={(e) => setNuevaLista((p) => ({ ...p, nombre_lista: e.target.value }))} />
            </div>
            <div>
              <Label className="mb-1 block text-xs">Tipo</Label>
              <Select value={nuevaLista.tipo} onValueChange={(v) => setNuevaLista((p) => ({ ...p, tipo: v }))}>
                <SelectTrigger data-testid="nueva-lista-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>{TIPOS_INTERNA.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenLista(false)}>Cancelar</Button>
            <Button data-testid="nueva-lista-guardar" onClick={crearLista} className="bg-primary hover:bg-sidebar-hover">Crear lista</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog nueva entrada interna */}
      <Dialog open={openEntrada} onOpenChange={setOpenEntrada}>
        <DialogContent className="sm:max-w-md" data-testid="nueva-entrada-dialog">
          <DialogHeader><DialogTitle className="font-heading">Agregar registro interno</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="mb-1 block text-xs">Lista</Label>
              <Select value={nuevaEntrada.lista_id} onValueChange={(v) => setNuevaEntrada((p) => ({ ...p, lista_id: v }))}>
                <SelectTrigger data-testid="nueva-entrada-lista"><SelectValue placeholder="Selecciona lista" /></SelectTrigger>
                <SelectContent>{internas.map((l) => <SelectItem key={l.id} value={l.id}>{l.nombre_lista}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1 block text-xs">Nombre completo / razón social</Label>
              <Input data-testid="nueva-entrada-nombre" value={nuevaEntrada.nombre}
                onChange={(e) => setNuevaEntrada((p) => ({ ...p, nombre: e.target.value }))} />
            </div>
            <div>
              <Label className="mb-1 block text-xs">RFC (opcional)</Label>
              <Input data-testid="nueva-entrada-rfc" value={nuevaEntrada.rfc}
                onChange={(e) => setNuevaEntrada((p) => ({ ...p, rfc: e.target.value }))} />
            </div>
            <div>
              <Label className="mb-1 block text-xs">Observaciones</Label>
              <Input value={nuevaEntrada.observaciones}
                onChange={(e) => setNuevaEntrada((p) => ({ ...p, observaciones: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenEntrada(false)}>Cancelar</Button>
            <Button data-testid="nueva-entrada-guardar" onClick={crearEntrada} className="bg-primary hover:bg-sidebar-hover">Agregar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const ESTATUS_LABEL = {
  pendiente: "Pendiente",
  confirmada: "Confirmada",
  descartada_falso_positivo: "Falso positivo",
  // oct-2026 · descarte en bloque por umbral de similitud (< 85) del Oficial de Cumplimiento.
  descartada: "Descartada (umbral)",
  // sep-2026 · la coincidencia de una versión revertida se CIERRA, nunca se borra.
  anulada_por_reversion: "Versión revertida",
};

// Origen de las altas de vigilancia manual: fuente a verificar antes de dictaminar.
function OrigenVigilancia({ extra }) {
  return (
      <div
        data-testid="match-fuente-vigilancia"
        className="mb-3 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2.5 text-xs text-indigo-900"
      >
        <p className="font-semibold uppercase tracking-wide">
          Origen del registro
        </p>
        <p className="mt-1 text-sm font-medium">
          {(extra.fuente_origen === "Otra" && extra.fuente_libre)
            || extra.fuente_origen
            || "origen no especificado"}
        </p>
        {extra.referencia_fuente && (
          <p className="mt-0.5 text-xs text-indigo-800">
            Referencia: <span className="font-mono">{extra.referencia_fuente}</span>
          </p>
        )}
        {extra.categoria_riesgo && (() => {
          const cat = extra.categoria_riesgo;
          const isNS = cat === "categoría no especificada";
          const isPep = extra.es_pep === true;
          let badgeCls = "border-red-300 bg-red-50 text-red-800";
          if (isNS) badgeCls = "border-slate-300 bg-slate-50 text-slate-500 italic";
          else if (isPep) badgeCls = "border-amber-400 bg-amber-50 text-amber-800";
          return (
            <div
              className="mt-2 border-t border-indigo-200 pt-2"
              data-testid="match-categoria-vigilancia"
            >
              <p className="text-[11px] font-semibold uppercase tracking-wide text-indigo-800">
                Categoría de riesgo
              </p>
              <span
                className={`mt-1 inline-block rounded-full border px-2.5 py-0.5 text-xs font-semibold ${badgeCls}`}
              >
                {cat}
              </span>
              {isPep && (
                <p className="mt-1.5 text-[11px] leading-relaxed text-amber-900">
                  PEP — No es un señalamiento negativo. Aplica{" "}
                  <b>debida diligencia reforzada</b> y documenta el{" "}
                  <b>origen de los recursos</b> conforme al régimen aplicable.
                </p>
              )}
            </div>
          );
        })()}
        {extra.motivo_alta && (
          <p className="mt-2 border-t border-indigo-200 pt-2 text-[11px] leading-relaxed text-indigo-800">
            <span className="font-semibold">Fundamento del alta:</span>{" "}
            {extra.motivo_alta}
          </p>
        )}
        <p className="mt-2 text-[11px] italic text-indigo-700">
          Usa esta fuente para verificar el registro original antes de confirmar o descartar.
        </p>
      </div>
  );
}
